import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import { type StatusTone } from "../../lib/tones.js";
import { type CategoricalTint } from "../../lib/tints.js";
import { renderMarker } from "./status-pill-marker.js";
import {
  pillColour,
  statusPillVariants,
  type StatusPillSize,
  type StatusPillVariant,
} from "./status-pill-variants.js";

export { statusPillVariants, type StatusPillSize, type StatusPillVariant };

/* ---------------- StatusPill ---------------- */

/* `as="button"`: the one focus treatment, the press step, a hover and press
   wash that layers over the fill (a background-image, so the tone text
   colour is kept), a pointer and Button's disabled look. */
const PILL_BUTTON =
  "focus-ring press cursor-pointer hover:pb-overlay-paint active:pb-overlay-paint-strong disabled:opacity-50 disabled:pointer-events-none";

/** The element the pill renders. `button` is for a pill that opens a menu or
 *  changes state; a read-only status stays a `span`. */
export type StatusPillElement = "span" | "button";

export interface StatusPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** @default "span". `as="button"` takes `StatusPillButtonProps`. */
  as?: "span" | undefined;
  tone?: StatusTone;
  /** @default "soft" */
  variant?: StatusPillVariant | undefined;
  /** A tint is decorative and never joins a tone: see `StatusPillTintProps`. */
  tint?: undefined;
  /** Replaces the leading dot, in an `aria-hidden` wrapper — `children`
   *  stays the pill's accessible name. `showDot={false}` hides it too. */
  icon?: React.ReactNode;
  /** Set false to render no leading marker at all (icon or dot). @default true */
  showDot?: boolean;
  /** @default "md" */
  size?: StatusPillSize;
  children: React.ReactNode;
  /** A span pill has no button attributes: they need `as="button"`. */
  type?: never;
  disabled?: never;
}

/** A categorical tint: decorative identity, not a state. Soft or outline only,
 *  never with a tone; module tints are chrome, so they are refused (ADR-0092). */
export interface StatusPillTintProps extends Omit<
  StatusPillProps,
  "tone" | "variant" | "tint"
> {
  tint: CategoricalTint;
  tone?: undefined;
  variant?: "soft" | "outline" | undefined;
}

/* The colour half of a button pill: a tone in any variant, or a tint in soft
   or outline, exactly as on the span forms. */
type PillColourProps =
  | Pick<StatusPillProps, "tone" | "variant" | "tint">
  | Pick<StatusPillTintProps, "tone" | "variant" | "tint">;

/** `as="button"`: a native `<button type="button">` with the focus ring, the
 *  press step, a hover wash that keeps the tone text colour, and Button's
 *  disabled look. It is not a toggle and has no pressed state (a toggle is
 *  Button `pressed`). The ref and rest props (aria-*, handlers) reach the
 *  button, so it works as a `PopoverTrigger` child. */
export type StatusPillButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> &
  Pick<StatusPillProps, "icon" | "showDot" | "size" | "children"> &
  PillColourProps & { as: "button" };

/**
 * Stable hook for "is there a pill here?", which is a question consumers'
 * suites genuinely need to ask and could previously only answer by probing a
 * Tailwind class (`[class*='rounded-full']`). That probe is wrong twice: it
 * matches any other round thing in the same subtree — an avatar, a status dot,
 * a count badge — and it breaks silently the moment the variant's utility list
 * changes, which is a styling edit nobody would expect to redden a behaviour
 * test. Same shape as `PresenceCluster`'s own `data-testid`s. Placed before the
 * rest spread so a consumer can still override it.
 */
export const STATUS_PILL_TEST_ID = "status-pill";

type AnyStatusPillProps =
  | StatusPillProps
  | StatusPillTintProps
  | StatusPillButtonProps;

/* The ref is an HTMLElement because the element varies (Card's precedent);
   an HTMLSpanElement ref object or callback still type-checks against it. */
export const StatusPill = forwardRef<HTMLElement, AnyStatusPillProps>(
  function StatusPill(
    {
      as: Element = "span",
      tone = "healthy",
      variant = "soft",
      tint,
      icon,
      showDot = true,
      size = "md",
      children,
      className,
      ...rest
    },
    ref,
  ) {
    const colour = pillColour(variant, tone, tint);
    const onFill = variant === "solid" && colour !== null && !tint;
    const button = Element === "button";
    return (
      <Element
        ref={ref as React.Ref<never>}
        {...(button ? { type: "button" as const } : {})}
        data-testid={STATUS_PILL_TEST_ID}
        className={cn(
          statusPillVariants({ tone: colour === null ? tone : null, size }),
          colour,
          button && PILL_BUTTON,
          className,
        )}
        {...(rest as React.HTMLAttributes<HTMLElement>)}
      >
        {renderMarker(tone, tint, onFill, size, icon, showDot)}
        {children}
      </Element>
    );
  },
);
